/**
 * Reloads a tab whose lazily loaded chunk no longer exists (PREC-4387).
 *
 * Every deploy renames the chunks, and the old ones are gone from the server.
 * A tab opened before the deploy still asks for the old names, so its first
 * `import()` after the deploy fails. Vite's preload helper announces that as a
 * `vite:preloadError` event on `window`; the only thing that fixes the tab is
 * fetching the new `index.html`, which is what a reload does.
 *
 * The error is deliberately not prevented. Prevented, the import resolves to
 * `undefined` and the lazy component crashes on that instead; let through, it
 * reaches the boundary around the component, which renders nothing until the
 * reload lands.
 */

/** sessionStorage key holding when this tab last reloaded for a stale chunk. */
export const STALE_CHUNK_RELOAD_KEY = "staleChunkReloadAt";

/**
 * A chunk that still fails this soon after our own reload is not stale, it is
 * missing — reloading again would loop.
 */
export const STALE_CHUNK_RELOAD_WINDOW_MS = 60_000;

/** Decides whether a chunk failure at `now` may reload, given the last reload's stamp. */
export const shouldReloadForStaleChunk = (lastReloadAt: string | null, now: number): boolean => {
	const last = Number(lastReloadAt);

	if (lastReloadAt === null || !Number.isFinite(last)) {
		return true;
	}

	return now - last > STALE_CHUNK_RELOAD_WINDOW_MS;
};

/** Stamps the reload first; answers false when it cannot, since nothing would then stop a loop. */
const claimReload = (storage: Pick<Storage, "getItem" | "setItem">, now: number): boolean => {
	try {
		if (!shouldReloadForStaleChunk(storage.getItem(STALE_CHUNK_RELOAD_KEY), now)) {
			return false;
		}

		storage.setItem(STALE_CHUNK_RELOAD_KEY, String(now));

		return true;
	} catch {
		return false;
	}
};

/** Listens for Vite's chunk-load failure and reloads the tab once. Call once at startup. */
export const installStaleChunkReload = (win: Window = window): void => {
	win.addEventListener("vite:preloadError", () => {
		if (claimReload(win.sessionStorage, Date.now())) {
			win.location.reload();
		}
	});
};
